
import React, { createContext, useContext, useState, useMemo } from 'react';

// Export the environment type to be used in other files
export type EnvironmentType = 'local' | 'development' | 'staging' | 'production';

interface EnvironmentContextProps {
  environment: EnvironmentType;
  setEnvironment: (env: EnvironmentType) => void;
  isDevelopment: boolean;
}

const EnvironmentContext = createContext<EnvironmentContextProps>({
  environment: 'production',
  setEnvironment: () => {},
  isDevelopment: false,
});

export function EnvironmentProvider({
  children,
  initialEnvironment = 'production',
}: {
  children: React.ReactNode;
  initialEnvironment?: EnvironmentType;
}) {
  // Try to get environment from .env file first
  const envFromVariable = import.meta.env.VITE_ENV as EnvironmentType | undefined;
  
  // Use either environment from .env or the provided initialEnvironment
  const [environment, setEnvironment] = useState<EnvironmentType>(
    envFromVariable || initialEnvironment
  );

  // Calculate whether we're in a development environment
  const isDevelopment = useMemo(() => {
    return environment === 'development' || environment === 'local';
  }, [environment]);

  // If environment is locked via .env, don't allow changing it
  const handleSetEnvironment = (env: EnvironmentType) => {
    if (!envFromVariable) {
      setEnvironment(env);
    } else {
      console.warn('Environment is locked via VITE_ENV in .env file');
    }
  };

  const value = {
    environment,
    setEnvironment: handleSetEnvironment,
    isDevelopment,
  };

  return <EnvironmentContext.Provider value={value}>{children}</EnvironmentContext.Provider>;
}

export function useEnvironment() {
  const context = useContext(EnvironmentContext);
  if (context === undefined) {
    throw new Error('useEnvironment must be used within an EnvironmentProvider');
  }
  return context;
}
